"use client";

import { useEffect, type RefObject } from "react";
import { buildLensFilter, isChromium, newLensId, removeLensFilter } from "@/lib/glass/refraction";
import { coarsePointer, reducedMotion, reducedTransparency, Spring, SPRING } from "@/lib/glass/spring";

/* ------------------------------------------------------------------ */
/* Lens: refraction in Chromium, blur-only everywhere else             */
/* ------------------------------------------------------------------ */

export interface LensOptions {
  /** Frost blur in px (2 to 6 keeps the scene visible). */
  frost?: number;
  /** Width of the bending band along the edge, px. */
  edge?: number;
  /** Maximum displacement at the rim, px. */
  strength?: number;
  /** Turn the lens off (the element keeps its CSS material). */
  enabled?: boolean;
}

/**
 * Gives a .glass element a lens: the SVG displacement map is built for its
 * exact size and radius and rebuilt when it resizes. Writes the result to
 * the --g-filter variable the material layer reads, so nothing else about
 * the element changes. With reduced transparency the CSS solid fallback
 * wins and no filter is built.
 */
export function useGlassLens(ref: RefObject<HTMLElement | null>, { frost = 3, edge = 14, strength = 11, enabled = true }: LensOptions = {}) {
  useEffect(() => {
    const el = ref.current;
    if (!el || !enabled) return;
    const id = newLensId();
    const refract = isChromium();
    let built = false;

    const apply = () => {
      if (reducedTransparency.get()) {
        el.style.removeProperty("--g-filter");
        return;
      }
      if (refract) built = buildLensFilter(el, id, { edge, strength });
      const value = refract && built ? `blur(${frost}px) url(#${id}) saturate(1.6)` : `blur(${frost}px) saturate(1.6)`;
      // Chromium caches a filter reference; clearing it first makes it pick
      // up a rebuilt map with the same id.
      el.style.setProperty("--g-filter", "none");
      void el.offsetWidth;
      el.style.setProperty("--g-filter", value);
    };

    let timer: ReturnType<typeof setTimeout> | undefined;
    const schedule = () => {
      clearTimeout(timer);
      timer = setTimeout(apply, 120);
    };

    apply();
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(schedule) : null;
    ro?.observe(el);
    const unsub = reducedTransparency.subscribe(apply);
    let alive = true;
    document.fonts?.ready.then(() => {
      if (alive) apply();
    });

    return () => {
      alive = false;
      clearTimeout(timer);
      ro?.disconnect();
      unsub();
      removeLensFilter(id);
      el.style.removeProperty("--g-filter");
    };
  }, [ref, frost, edge, strength, enabled]);
}

/* ------------------------------------------------------------------ */
/* Press swell                                                         */
/* ------------------------------------------------------------------ */

/**
 * Press: the element swells to `scale` on a spring (response 0.25, damping
 * 0.78, 2% overshoot) and its rim brightens; release springs it back. Uses
 * the standalone `scale` property so a CSS hover lift (translate) never
 * fights it. Keyboard presses (Space / Enter) get the same feedback.
 */
export function usePressSwell(ref: RefObject<HTMLElement | null>, { scale = 1.08, enabled = true }: { scale?: number; enabled?: boolean } = {}) {
  useEffect(() => {
    const el = ref.current;
    if (!el || !enabled) return;
    const s = new Spring(1, { ...SPRING.PRESS, epsilon: 0.0005 });
    s.onUpdate = () => {
      el.style.scale = Math.abs(s.x - 1) < 0.0005 ? "" : s.x.toFixed(4);
    };
    let keyTimer: ReturnType<typeof setTimeout> | undefined;
    const down = () => {
      el.style.setProperty("--rim-k", "1.8");
      if (!reducedMotion.get()) s.to(scale);
    };
    const up = () => {
      el.style.removeProperty("--rim-k");
      s.to(1);
    };
    const onDown = (e: PointerEvent) => {
      if (e.button === 0) down();
    };
    const onKey = (e: KeyboardEvent) => {
      if ((e.key === " " || e.key === "Enter") && !e.repeat) {
        down();
        clearTimeout(keyTimer);
        keyTimer = setTimeout(up, 150);
      }
    };
    el.addEventListener("pointerdown", onDown);
    const ups = ["pointerup", "pointerleave", "pointercancel", "blur"] as const;
    ups.forEach((t) => el.addEventListener(t, up));
    el.addEventListener("keydown", onKey);
    return () => {
      clearTimeout(keyTimer);
      s.stop();
      el.removeEventListener("pointerdown", onDown);
      ups.forEach((t) => el.removeEventListener(t, up));
      el.removeEventListener("keydown", onKey);
      el.style.scale = "";
      el.style.removeProperty("--rim-k");
    };
  }, [ref, scale, enabled]);
}

/* ------------------------------------------------------------------ */
/* Rim follows the pointer                                             */
/* ------------------------------------------------------------------ */

/**
 * Turns the rim highlight of the glass surfaces inside `rootRef` toward the
 * pointer (writes --rim-a on each). One listener per root, throttled to one
 * update per frame, only for surfaces on screen. Off on touch screens and
 * with reduced motion.
 */
export function useRimFollow(rootRef: RefObject<HTMLElement | null>, selector = ".glass") {
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    let frame = 0;
    let ev: PointerEvent | null = null;
    let touched: HTMLElement[] = [];

    const update = () => {
      frame = 0;
      if (!ev) return;
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const next: HTMLElement[] = [];
      root.querySelectorAll<HTMLElement>(selector).forEach((el) => {
        const r = el.getBoundingClientRect();
        if (r.bottom < 0 || r.top > vh || r.right < 0 || r.left > vw) return;
        const dx = Math.max(-1, Math.min(1, (ev!.clientX - (r.left + r.width / 2)) / Math.max(r.width, 80)));
        const dy = Math.max(-1, Math.min(1, (ev!.clientY - (r.top + r.height / 2)) / Math.max(r.height, 80)));
        el.style.setProperty("--rim-a", `${(135 - dx * 18 + dy * 8).toFixed(1)}deg`);
        next.push(el);
      });
      touched.forEach((el) => {
        if (!next.includes(el)) el.style.removeProperty("--rim-a");
      });
      touched = next;
    };
    const onMove = (e: PointerEvent) => {
      if (e.pointerType !== "mouse" || reducedMotion.get() || coarsePointer.get()) return;
      ev = e;
      if (!frame) frame = requestAnimationFrame(update);
    };
    const reset = () => {
      ev = null;
      touched.forEach((el) => el.style.removeProperty("--rim-a"));
      touched = [];
    };
    root.addEventListener("pointermove", onMove, { passive: true });
    root.addEventListener("pointerleave", reset);
    return () => {
      cancelAnimationFrame(frame);
      root.removeEventListener("pointermove", onMove);
      root.removeEventListener("pointerleave", reset);
      reset();
    };
  }, [rootRef, selector]);
}
